/**
 * Alatreyu Review Intercept
 *
 * Watches for review app widgets (Judge.me, Shopify Product Reviews, Yotpo, etc.)
 * and intercepts 1-2 star selections with a friendly "tell us what happened" modal.
 *
 * Design principles:
 * - Never blocks review submission ("Submit as-is" always works)
 * - Only triggers once per session (sessionStorage)
 * - Review-app-agnostic via MutationObserver + common star selectors
 * - Accessible: <dialog>, focus trap, Escape to close
 */
(function () {
  'use strict';

  var SESSION_KEY = 'alatreyu_review_intercept_shown';
  var LOW_RATING_THRESHOLD = 2; // 1 or 2 stars triggers the intercept

  // Common selectors for review star elements across popular Shopify review apps
  var STAR_SELECTORS = [
    '.jdgm-star',                          // Judge.me
    '.jdgm-form__star-rating a',           // Judge.me form stars
    '.spr-icon-star',                      // Shopify Product Reviews
    '.spr-starrating .spr-icon',           // SPR alt
    '.stamped-star-rating .stamped-fa',    // Stamped.io
    '.yotpo-star',                         // Yotpo
    '.loox-star',                          // Loox
    '[data-star-rating] [data-score]',     // Generic data-attribute pattern
    '.review-star[data-value]',            // Generic class pattern
    '.star-rating__star',                  // Common custom pattern
  ];

  var combinedSelector = STAR_SELECTORS.join(', ');
  var modal = null;
  var observerActive = false;
  var pendingTimeout = null;

  /**
   * Extract the numeric star rating from a clicked star element.
   * Different review apps store this differently.
   */
  function getStarValue(el) {
    // data-score (Judge.me)
    var score = el.getAttribute('data-score');
    if (score) return parseInt(score, 10);

    // data-value (generic)
    var value = el.getAttribute('data-value');
    if (value) return parseInt(value, 10);

    // Position-based: count preceding siblings of same type
    var parent = el.parentElement;
    if (parent) {
      var stars = parent.querySelectorAll(el.tagName.toLowerCase());
      for (var i = 0; i < stars.length; i++) {
        if (stars[i] === el) return i + 1;
      }
    }

    return 0;
  }

  /**
   * Show the intercept modal
   */
  function showModal() {
    if (!modal) return;
    if (typeof modal.showModal === 'function') {
      modal.showModal();
    }
  }

  /**
   * Close the intercept modal
   */
  function closeModal() {
    if (!modal) return;
    if (typeof modal.close === 'function') {
      modal.close();
    }
  }

  /**
   * Handle a star click event
   */
  function handleStarClick(e) {
    // Already shown this session? Skip.
    if (sessionStorage.getItem(SESSION_KEY)) return;

    // Find the star element (may need to walk up from SVG child, etc.)
    var target = e.target;
    var starEl = target.closest(combinedSelector);
    if (!starEl) return;

    var rating = getStarValue(starEl);
    if (rating < 1 || rating > LOW_RATING_THRESHOLD) return;

    // Mark as shown so we don't interrupt again this session
    sessionStorage.setItem(SESSION_KEY, '1');

    // Small delay to let the star animation complete
    if (pendingTimeout) clearTimeout(pendingTimeout);
    pendingTimeout = setTimeout(function () {
      showModal();
      pendingTimeout = null;
    }, 300);
  }

  /**
   * Attach click handlers to star elements within a container
   */
  function attachStarListeners(container) {
    var stars = container.querySelectorAll(combinedSelector);
    stars.forEach(function (star) {
      if (!star.hasAttribute('data-alatreyu-intercept')) {
        star.setAttribute('data-alatreyu-intercept', 'bound');
        star.addEventListener('click', handleStarClick);
      }
    });
  }

  /**
   * Watch for review widgets being injected into the DOM
   */
  function startObserver() {
    if (observerActive) return;
    observerActive = true;

    // First pass: check for any existing star elements
    attachStarListeners(document.body);

    // Watch for future injections (review apps load asynchronously)
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var mutation = mutations[i];
        if (mutation.type !== 'childList' || !mutation.addedNodes.length) continue;

        for (var j = 0; j < mutation.addedNodes.length; j++) {
          var node = mutation.addedNodes[j];
          if (node.nodeType !== 1) continue; // Element nodes only

          // Check if the added node itself is a star
          if (node.matches && node.matches(combinedSelector)) {
            if (!node.hasAttribute('data-alatreyu-intercept')) {
              node.setAttribute('data-alatreyu-intercept', 'bound');
              node.addEventListener('click', handleStarClick);
            }
          }

          // Check children of the added node
          if (node.querySelectorAll) {
            attachStarListeners(node);
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Initialize the review intercept system
   */
  function init() {
    // Only run on product pages
    if (!document.querySelector('[data-product-id], .product, .product-single, main-product')) return;

    modal = document.getElementById('ReviewInterceptModal');
    if (!modal) return;

    // "Submit as-is" button — closes modal, review form stays active
    var submitBtn = modal.querySelector('[data-review-intercept-submit]');
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        closeModal();
      });
    }

    // Close button
    var closeBtn = modal.querySelector('[data-review-intercept-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        closeModal();
      });
    }

    // Escape key handled natively by <dialog>

    // Click on backdrop closes modal
    modal.addEventListener('click', function (e) {
      if (e.target === modal) {
        closeModal();
      }
    });

    // Start watching for review widgets
    startObserver();
  }

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize when Shopify theme editor loads sections
  document.addEventListener('shopify:section:load', function () {
    init();
  });
})();
